<template>
  <div>
    <FilterEvaluate @search="onSearch" />
    <v-card class="mt-4 elevation-2">
      <v-card-title class="headline font-weight-bold d-flex justify-space-between align-center mx-3">
        <span class="mt-4">Danh sách đánh giá</span>
      </v-card-title>
      <v-card-text class="px-3">
        <v-container fluid v-if="evaluations.length > 0">
          <v-row>
            <v-col v-for="(evaluation, index) in evaluations" :key="index" cols="12" sm="6" md="3">
              <v-card outlined class="mb-4 rounded-lg hover-elevation-2">
                <v-card-title class="primary white--text">
                  <v-icon dark small class="mr-2">mdi-clipboard-text-outline</v-icon>
                  <div class="text-truncate text-uppercase">{{ getFormattedTitle(evaluation) }}</div>
                </v-card-title>
                <v-card-text class="px-0">
                  <v-list-item two-line>
                    <v-list-item-content>
                      <v-list-item-subtitle class="text-subtitle-1 text-truncate font-weight-bold">
                        {{ evaluation.title }}
                      </v-list-item-subtitle>
                      <v-divider class="my-2"></v-divider>
                      <v-list-item-subtitle class="text-subtitle-1 mb-1 text-truncate">
                        <v-icon small color="indigo">mdi-teach</v-icon>
                        Lớp: {{ evaluation.class.title }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle class="text-body-2 mb-1">
                        <v-icon small color="green">mdi-clock-outline</v-icon>
                        Thời gian: {{ formatDate(evaluation.time) }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle class="text-body-2 mb-1">
                        <v-icon small color="orange">mdi-format-list-bulleted-type</v-icon>
                        Loại: {{ getEvaluationType(evaluation.type) }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle class="text-body-2 blue--text">
                        <v-icon small color="blue">mdi-check-circle-outline</v-icon>
                        Đã đánh giá: {{ evaluation.totalEvaluated }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-card-text>
                <v-card-actions>
                  <v-btn text color="primary" @click="openEvaluateStudentDialog(evaluation)"> Đánh giá </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn icon color="primary" @click="showEvaluationDetails(evaluation)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-menu offset-y :close-on-content-click="false">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small>
                        <v-icon color="#0D47A1">mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list class="py-0">
                      <ExportEvaluate :evaluate-id="evaluation.id" style="width: 100%;" />
                      <v-divider/>
                      <SendMessageEvaluate :evaluate-id="evaluation.id" />
                    </v-list>
                  </v-menu>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
        <v-alert v-else type="info" outlined class="mt-4"> Không có dữ liệu đánh giá. Vui lòng tạo mới. </v-alert>
      </v-card-text>
      <v-card-actions class="justify-center" v-if="evaluations.length > 0">
        <div class="d-flex align-center">
          <v-btn icon :disabled="currentPage === 1 || loading" @click="onPageChange(currentPage - 1)" color="primary">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <v-btn
            v-for="page in getVisiblePages" 
            :key="page"
            text
            :loading="loading && page === currentPage"
            :disabled="loading"
            :color="page === currentPage ? 'primary' : ''"
            :class="{ 'font-weight-bold': page === currentPage }"
            @click="onPageChange(page)"
            class="mx-1"
            min-width="32"
          >
            {{ page }}
          </v-btn>

          <v-btn icon :disabled="currentPage === totalPages || loading" @click="onPageChange(currentPage + 1)" color="primary">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>

    <DialogDetailEvaluate
      ref="dialogDetailEvaluate"
      :selectedEvaluation="selectedEvaluation"
      @update-title="updateEvaluationTitle"
      @delete-evaluation="deleteEvaluation"
    />
    <DialogEvaluateStudent ref="dialogEvaluateStudent" />
  </div>
</template>

<script>
import FilterEvaluate from './FilterEvaluate.vue'
import DialogDetailEvaluate from './DialogDetailEvaluate.vue'
import DialogEvaluateStudent from './DialogEvaluateStudent.vue'
import { mapState, mapActions } from 'vuex'
import SendMessageEvaluate from './SendMessageEvaluate.vue'
import ExportEvaluate from './ExportEvaluate.vue'
import dayjs from 'dayjs'

export default {
  components: {
    FilterEvaluate,
    DialogDetailEvaluate,
    DialogEvaluateStudent,
    SendMessageEvaluate,
    ExportEvaluate,
  },
  data() {
    return {
      selectedEvaluation: null,
      currentPage: 1,
      itemsPerPage: 10,
      searchParams: {},
    }
  },
  computed: {
    ...mapState('evaluate', ['evaluates', 'loading', 'error', 'total']),
    evaluations() {
      return this.evaluates
    },
    totalPages() {
      return Math.ceil(this.total / this.itemsPerPage) || 1
    },
    getVisiblePages() {
      const maxVisiblePages = 5
      const halfVisible = Math.floor(maxVisiblePages / 2)
      let startPage = Math.max(1, this.currentPage - halfVisible)
      let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1)

      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1)
      }

      const pages = []
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i)
      }
      return pages
    }
  },
  methods: {
    ...mapActions('evaluate', ['fetchEvaluates', 'countEvaluates', 'updateEvaluate', 'deleteEvaluate']),
    async onSearch(searchParams) {
      this.searchParams = searchParams
      this.currentPage = 1
      await this.fetchData()
    },
    async fetchData() {
      try {
        const params = {
          ...this.searchParams,
          _start: (this.currentPage - 1) * this.itemsPerPage,
          _limit: this.itemsPerPage,
          _sort: 'time:desc',
          time_lte: dayjs().endOf('day').toISOString()
        }
        await Promise.all([this.fetchEvaluates(params), this.countEvaluates(this.searchParams)])
      } catch (error) {
        console.error('Error fetching evaluates:', error)
      }
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleString('vi-VN')
    },
    getEvaluationType(type) {
      const types = {
        session: 'Buổi học',
        week: 'Tuần',
        month: 'Tháng',
        custom: 'Tùy chỉnh',
      }
      return types[type] || type
    },
    showEvaluationDetails(evaluation) {
      this.selectedEvaluation = evaluation
      this.$refs.dialogDetailEvaluate.openDialog()
    },
    getFormattedTitle(evaluation) {
      const type = this.getEvaluationType(evaluation.type)
      const index = evaluation.index ? ` ${evaluation.index + 1}` : ' 1'
      return `${evaluation.class.title} ${type}${index}`
    },
    async onPageChange(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        await this.fetchData()
      }
    },
    async updateEvaluationTitle({ id, newTitle }) {
      try {
        const evaluateData = { title: newTitle }
        await this.updateEvaluate({ id, evaluateData })
        await this.fetchData()
      } catch (error) {
        console.error('Error updating evaluation title:', error)
      }
    },
    async deleteEvaluation(id) {
      try {
        await this.deleteEvaluate(id)
        await this.fetchData()
      } catch (error) {
        console.error('Error deleting evaluation:', error)
      }
    },
    openEvaluateStudentDialog(evaluation) {
      this.$refs.dialogEvaluateStudent.openDialog(evaluation)
    },
  },
  created() {
    this.fetchData()
  },
}
</script>
